import { useEffect, useMemo, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetSubscriptionsByOrganizationQuery,
  useGetPlansQuery,
  useRequestSubscriptionMutation,
  useGetSubscriptionUpgradeRequestsByOrganizationQuery,
  useCancelSubscriptionUpgradeRequestMutation,
} from "../../redux/api";
import type { OrganizationSubscription } from "../../redux/api";
import { IconChevronDown, IconChevronRight } from "../../components/icons";
import { subscriptionStatusTone } from "../../utils/status";
import { priceLabel } from "../../utils/planPricing";
import {
  planLimitRows,
  planLimitValueLabel,
  isNegotiatedPlan,
} from "../../utils/planLimits";

const money = (value?: number | null, currency?: string) =>
  typeof value === "number"
    ? `${currency ? `${currency} ` : ""}${value.toLocaleString()}`
    : "—";

const date = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : "—";

// Owner → My Subscription. Read-only view of the current organization's
// subscription(s) — owners can see their plan and billing status, but only the
// platform owner can create or change a subscription.
const MySubscription = () => {
  const { data: organizations = [], isLoading: loadingOrgs } =
    useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();

  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && organizations.length) setOrgId(organizations[0].id);
  }, [organizations, orgId]);

  const {
    data: subscriptions = [],
    isLoading: loadingSubs,
    isFetching,
  } = useGetSubscriptionsByOrganizationQuery(orgId, { skip: !orgId });

  const planName = (planId: string) =>
    plans.find((p) => p.id === planId)?.name ?? planId;

  const selected = organizations.find((o) => o.id === orgId);
  const expired = (selected?.status ?? "").toLowerCase() === "expired";

  const [requestSubscription, { isLoading: requestingPlan }] =
    useRequestSubscriptionMutation();
  const [cancelRequest, { isLoading: cancelling }] =
    useCancelSubscriptionUpgradeRequestMutation();
  const [requestError, setRequestError] = useState<string | null>(null);

  // Persisted, not local component state — survives a refresh instead of
  // just disabling the button for the current session.
  const { data: myRequests = [] } = useGetSubscriptionUpgradeRequestsByOrganizationQuery(orgId, {
    skip: !orgId,
  });
  const pendingRequestForPlan = (planId: string) =>
    myRequests.find((r) => r.planId === planId && r.status === "pending");

  // Most recently declined request, if its feedback hasn't been superseded by
  // a newer resolved one — surfaced so the owner knows why and can adjust
  // their next ask (mainly useful for a Custom-plan counter-offer).
  const lastRejected = useMemo(
    () =>
      [...myRequests]
        .filter((r) => r.status === "rejected")
        .sort((a, b) => (b.resolvedAt ?? "").localeCompare(a.resolvedAt ?? ""))[0],
    [myRequests],
  );

  // CUSTOM-code plans have no fixed price — the owner proposes one (plus an
  // optional note) when requesting, keyed by plan id since there could in
  // theory be more than one Custom plan.
  const [customAmount, setCustomAmount] = useState<Record<string, string>>({});
  const [customNote, setCustomNote] = useState<Record<string, string>>({});

  // Per-plan, per-field validation messages — shown directly under the
  // offending input (with a red border) instead of a single generic banner,
  // so it's obvious exactly what needs fixing.
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, { amount?: string; note?: string }>
  >({});

  const handleRequestPlan = async (planId: string) => {
    setRequestError(null);
    const plan = plans.find((p) => p.id === planId);
    const isCustom = plan?.code === "CUSTOM";

    const note = customNote[planId]?.trim();
    const rawAmount = isCustom ? customAmount[planId]?.trim() : undefined;
    const parsedAmount = rawAmount ? Number(rawAmount) : undefined;

    const errors: { amount?: string; note?: string } = {};
    // A note is required for every plan request — the platform owner has no
    // other way to know why an org wants the change.
    if (!note) errors.note = "Please add a note explaining this request.";
    // Custom additionally requires a proposed price (it has no fixed one).
    if (isCustom) {
      if (!rawAmount) {
        errors.amount = "Enter your proposed price.";
      } else if (!Number.isFinite(parsedAmount) || (parsedAmount as number) <= 0) {
        errors.amount = "Enter a valid proposed price.";
      }
    }

    if (errors.amount || errors.note) {
      setFieldErrors((prev) => ({ ...prev, [planId]: errors }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, [planId]: {} }));

    try {
      await requestSubscription({
        organizationId: orgId,
        planId,
        proposedAmount: isCustom ? parsedAmount : undefined,
        proposedCurrency: isCustom ? (current?.currency ?? "INR") : undefined,
        requestNote: note,
      }).unwrap();
    } catch (err) {
      setRequestError(
        (err as { message?: string })?.message ?? "Failed to send request.",
      );
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!window.confirm("Withdraw this plan request?")) return;
    setRequestError(null);
    try {
      await cancelRequest(requestId).unwrap();
    } catch (err) {
      setRequestError(
        (err as { message?: string })?.message ?? "Failed to cancel request.",
      );
    }
  };

  // Newest first. "Current" is whichever is active; if none is (e.g. between
  // renewals) fall back to the most recent one so the screen never looks empty.
  const sorted = useMemo(
    () =>
      [...subscriptions].sort((a, b) =>
        (b.startDate ?? "").localeCompare(a.startDate ?? ""),
      ),
    [subscriptions],
  );
  const current =
    sorted.find((s) => (s.status ?? "").toLowerCase() === "active") ??
    sorted[0];
  const history = sorted.filter((s) => s.id !== current?.id);

  const [expandedId, setExpandedId] = useState<string | null>(null);

  const subscriptionFields = (sub: OrganizationSubscription) => (
    <div className="form-grid">
      <label className="field">
        Plan
        <input value={planName(sub.planId)} disabled />
      </label>
      <label className="field">
        Status
        <div>
          <span className={`badge badge--${subscriptionStatusTone(sub.status)}`}>
            <span className="badge__dot" />
            {sub.status}
          </span>
        </div>
      </label>
      <label className="field">
        Billing cycle
        <input value={sub.billingCycle} disabled />
      </label>
      <label className="field">
        Amount paid
        <input value={money(sub.amountPaid, sub.currency)} disabled />
      </label>
      <label className="field">
        Auto-renew
        <input value={sub.autoRenew ? "Yes" : "No"} disabled />
      </label>
      <label className="field">
        Start date
        <input value={date(sub.startDate)} disabled />
      </label>
      <label className="field">
        End date
        <input value={date(sub.endDate)} disabled />
      </label>
      {sub.remarks ? (
        <label className="field field--full">
          Remarks
          <input value={sub.remarks} disabled />
        </label>
      ) : null}
    </div>
  );

  return (
    <div className="dashboard">
      {orgId ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2>{expired ? "Renew your subscription" : "Request a plan change"}</h2>
              <p className="panel__sub">
                {expired
                  ? "This organization's subscription has expired. Choose a plan to request renewal — your platform administrator will be notified and can activate it."
                  : "Want to upgrade, downgrade, or switch plans? Choose one below — your platform administrator will be notified and can activate it."}
              </p>
            </div>
          </div>
          <div className="panel__body">
            {lastRejected ? (
              <div className="form-error">
                Your request for {planName(lastRejected.planId)} was declined
                {lastRejected.note ? `: ${lastRejected.note}` : "."}
              </div>
            ) : null}
            {requestError ? (
              <div className="form-error">{requestError}</div>
            ) : null}
            {plans.filter((p) => p.isActive).length === 0 ? (
              <p className="profile-empty">No plans are available right now.</p>
            ) : (
              <div className="plan-grid">
                {plans
                  .filter((p) => p.isActive)
                  .map((plan) => {
                    const pending = pendingRequestForPlan(plan.id);
                    // Requesting the plan you're already actively on is a
                    // no-op — except when expired, where re-requesting the
                    // same plan is how a renewal works.
                    const isCurrentPlan = !expired && plan.id === current?.planId;
                    const isCustom = plan.code === "CUSTOM";
                    return (
                      <div className="plan-card" key={plan.id}>
                        <h4>{plan.name}</h4>
                        <p className="plan-card__price">
                          {priceLabel(plan, "IN")}
                          {plan.code !== "CUSTOM" ? "/mo" : ""}
                        </p>
                        {plan.description ? (
                          <p className="plan-card__desc">{plan.description}</p>
                        ) : null}

                        <div className="plan-limits">
                          <p className="plan-limits__title">WHAT&apos;S INCLUDED</p>
                          {planLimitRows(plan).map((row) => (
                            <div className="plan-limits__row" key={row.id}>
                              <span className="plan-limits__label">{row.label}</span>
                              <span className="plan-limits__value">
                                {planLimitValueLabel(row)}
                              </span>
                            </div>
                          ))}
                          {isNegotiatedPlan(plan) ? (
                            <p className="plan-limits__note">
                              Limits on a Custom plan are agreed with the platform owner,
                              not fixed here.
                            </p>
                          ) : null}
                        </div>

                        {!pending && !isCurrentPlan ? (
                          <div style={{ marginBottom: "0.75rem" }}>
                            {isCustom ? (
                              <label
                                className={`field${fieldErrors[plan.id]?.amount ? " field--invalid" : ""}`}
                              >
                                Your proposed price ({current?.currency ?? "INR"}/mo) *
                                <input
                                  type="number"
                                  min="0"
                                  value={customAmount[plan.id] ?? ""}
                                  onChange={(e) => {
                                    setCustomAmount((prev) => ({ ...prev, [plan.id]: e.target.value }));
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      [plan.id]: { ...prev[plan.id], amount: undefined },
                                    }));
                                  }}
                                  placeholder="e.g. 5000"
                                />
                                {fieldErrors[plan.id]?.amount ? (
                                  <span className="field__error">{fieldErrors[plan.id].amount}</span>
                                ) : null}
                              </label>
                            ) : null}
                            <label className={`field${fieldErrors[plan.id]?.note ? " field--invalid" : ""}`}>
                              Note *
                              <input
                                value={customNote[plan.id] ?? ""}
                                onChange={(e) => {
                                  setCustomNote((prev) => ({ ...prev, [plan.id]: e.target.value }));
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    [plan.id]: { ...prev[plan.id], note: undefined },
                                  }));
                                }}
                                placeholder="Tell us why you want this plan…"
                              />
                              {fieldErrors[plan.id]?.note ? (
                                <span className="field__error">{fieldErrors[plan.id].note}</span>
                              ) : null}
                            </label>
                          </div>
                        ) : null}

                        {isCurrentPlan ? (
                          <button type="button" className="btn btn--primary" disabled>
                            Current plan
                          </button>
                        ) : pending ? (
                          <>
                            <button type="button" className="btn btn--primary" disabled>
                              Pending platform owner approval
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost"
                              onClick={() => handleCancelRequest(pending.id)}
                              disabled={cancelling}
                            >
                              Cancel request
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--primary"
                            onClick={() => handleRequestPlan(plan.id)}
                            disabled={requestingPlan}
                          >
                            Request this plan
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </section>
      ) : null}

      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Subscription</h2>
            <p className="panel__sub">
              {selected ? selected.name : "Select an organization"}
            </p>
          </div>
          {organizations.length > 1 && (
            <div className="toolbar">
              <select
                className="select"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="panel__body">
          {loadingOrgs || loadingSubs || isFetching ? (
            <div className="profile-empty">Loading…</div>
          ) : !orgId ? (
            <div className="profile-empty">
              No organization selected. Create one from the Platform Owner
              area first.
            </div>
          ) : sorted.length === 0 ? (
            <div className="profile-empty">
              No subscription found for this organization.
            </div>
          ) : (
            <>
              {current ? (
                <>
                  <h3 className="panel__section-title" style={{ marginTop: 0 }}>
                    Current subscription
                  </h3>
                  {subscriptionFields(current)}
                </>
              ) : null}

              {history.length > 0 ? (
                <>
                  <h3 className="panel__section-title">
                    Subscription history
                  </h3>
                  <ul className="timeline">
                    {history.map((sub) => {
                      const isOpen = expandedId === sub.id;
                      return (
                        <li className="timeline__item" key={sub.id}>
                          <button
                            type="button"
                            className="timeline__row"
                            onClick={() =>
                              setExpandedId(isOpen ? null : sub.id)
                            }
                            aria-expanded={isOpen}
                          >
                            <span className="timeline__dot" />
                            <span className="timeline__label">
                              <strong>{planName(sub.planId)}</strong>
                              <span className="timeline__dates">
                                {date(sub.startDate)} – {date(sub.endDate)}
                              </span>
                            </span>
                            <span className={`badge badge--${subscriptionStatusTone(sub.status)}`}>
                              <span className="badge__dot" />
                              {sub.status}
                            </span>
                            {isOpen ? (
                              <IconChevronDown size={16} />
                            ) : (
                              <IconChevronRight size={16} />
                            )}
                          </button>
                          {isOpen ? (
                            <div className="timeline__details">
                              {subscriptionFields(sub)}
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default MySubscription;
